import { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { MdClose } from "react-icons/md";
import { useQuery } from "@tanstack/react-query";
import baggageStatsService from "services/admin/baggageStatsService";
import ChartCanvas from "./ChartCanvas";

interface Props {
  from: string;
  to: string;
}

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "tonnage", label: "По весу" },
  { value: "revenuePaid", label: "По выручке" },
  { value: "revenuePerKg", label: "Дешёвые (₸/кг ↑)" },
  { value: "problemRate", label: "Проблемные" },
  { value: "tripsCount", label: "По числу отправок" },
];

/**
 * Аналитика маршрутов (Фаза 1): OD-матрица направлений, сводка, баланс
 * встречных направлений (проксИ порожнего пробега) и детализация маршрута
 * в боковой панели. Период наследуется от родителя (по createdAt отправки).
 */
const RouteAnalytics: React.FC<Props> = ({ from, to }) => {
  const orgID = localStorage.getItem("orgID") || "";
  const [sort, setSort] = useState("tonnage");
  const [collapse, setCollapse] = useState(false);
  const [minTrips, setMinTrips] = useState(1);
  const [selected, setSelected] = useState<{ originID: string; destID: string; title: string } | null>(
    null,
  );

  const { data, isLoading } = useQuery({
    queryKey: ["baggage-route-stats", { orgID, from, to, sort, collapse, minTrips }],
    queryFn: async () =>
      (
        await baggageStatsService.getRouteStats({
          from,
          to,
          sort,
          collapseDirections: collapse ? "1" : "",
          minTrips: String(minTrips),
        })
      ).data,
  });

  const currency = data?.currency || "KZT";
  const money = (n: number) => `${Number(n || 0).toLocaleString("ru-RU")} ${currency}`;
  const num = (n: number) => Number(n || 0).toLocaleString("ru-RU");
  const pct = (n: number) => `${Math.round(Number(n || 0) * 100)}%`;

  const routes: any[] = data?.routes || [];
  const balance: any[] = data?.balance || [];
  const summary = data?.summary;
  const medianPerKg = summary?.medianRevenuePerKg || 0;

  // Топ/антитоп: самые дешёвые (₸/кг) и самые проблемные маршруты.
  const cheapest = useMemo(
    () =>
      [...routes]
        .filter((r) => r.revenuePerKg > 0)
        .sort((a, b) => a.revenuePerKg - b.revenuePerKg)
        .slice(0, 3),
    [routes],
  );
  const mostProblematic = useMemo(
    () =>
      [...routes]
        .filter((r) => r.problemRate > 0)
        .sort((a, b) => b.problemRate - a.problemRate)
        .slice(0, 3),
    [routes],
  );

  const hasData = routes.length > 0;

  return (
    <div>
      {/* Управление */}
      <div
        className="m-b-16"
        style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}
      >
        <TextField
          select
          size="small"
          label="Сортировка"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          label="Мин. отправок"
          value={minTrips}
          onChange={(e) => setMinTrips(Math.max(1, Number(e.target.value) || 1))}
          sx={{ width: 130 }}
          inputProps={{ min: 1 }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={collapse ? "yes" : "no"}
          onChange={(_e, v) => v && setCollapse(v === "yes")}
        >
          <ToggleButton value="no">A→B</ToggleButton>
          <ToggleButton value="yes">A↔B</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {isLoading ? (
        <div className="text-center m-t-32">
          <CircularProgress />
        </div>
      ) : !hasData ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              За выбранный период нет маршрутов. Измените диапазон или фильтр «мин. отправок».
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Сводка */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 2,
              mb: 3,
            }}
          >
            {[
              { label: "Маршрутов", value: num(summary.routesCount) },
              { label: "Отправок", value: num(summary.tripsCount) },
              { label: "Вес, кг", value: num(summary.totalWeightKg) },
              { label: "Выручка (оплачено)", value: money(summary.revenuePaid), accent: "#2dd36f" },
              { label: "Медиана ₸/кг", value: num(summary.medianRevenuePerKg) },
              {
                label: "Без маршрута",
                value: num(summary.unroutableTrips),
                accent: summary.unroutableTrips ? "#ffc409" : undefined,
              },
            ].map((c: any) => (
              <Card key={c.label} variant="outlined">
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {c.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: c.accent || "inherit" }}>
                    {c.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Топ/антитоп */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 3,
              mb: 3,
            }}
          >
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Самые «дешёвые» маршруты (₸/кг)
                </Typography>
                {cheapest.length ? (
                  cheapest.map((r) => (
                    <div
                      key={`${r.originStationID}|${r.destinationStationID}`}
                      style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}
                    >
                      <span>
                        {r.originName} → {r.destinationName}
                      </span>
                      <strong>{num(r.revenuePerKg)} ₸/кг</strong>
                    </div>
                  ))
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    Нет оплаченных маршрутов.
                  </Typography>
                )}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Самые проблемные маршруты
                </Typography>
                {mostProblematic.length ? (
                  mostProblematic.map((r) => (
                    <div
                      key={`${r.originStationID}|${r.destinationStationID}`}
                      style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}
                    >
                      <span>
                        {r.originName} → {r.destinationName}
                      </span>
                      <strong style={{ color: "#eb445a" }}>{pct(r.problemRate)}</strong>
                    </div>
                  ))
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    Проблемных мест нет.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* OD-матрица */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Маршруты {collapse ? "(встречные сведены)" : ""}
              </Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Маршрут</TableCell>
                      <TableCell align="right">Отпр.</TableCell>
                      <TableCell align="right">Мест</TableCell>
                      <TableCell align="right">Вес, кг</TableCell>
                      <TableCell align="right">Выручка</TableCell>
                      <TableCell align="right">₸/кг</TableCell>
                      <TableCell align="right">Не опл.</TableCell>
                      <TableCell align="right">Проблем.</TableCell>
                      <TableCell align="right">Выдано</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {routes.map((r) => {
                      const cheap = r.revenuePerKg > 0 && medianPerKg > 0 && r.revenuePerKg < medianPerKg;
                      const problem = r.problemRate >= 0.05;
                      return (
                        <TableRow
                          key={`${r.originStationID}|${r.destinationStationID}`}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() =>
                            setSelected({
                              originID: r.originStationID,
                              destID: r.destinationStationID,
                              title: `${r.originName} → ${r.destinationName}`,
                            })
                          }
                        >
                          <TableCell>
                            {r.originName} → {r.destinationName}
                          </TableCell>
                          <TableCell align="right">{num(r.tripsCount)}</TableCell>
                          <TableCell align="right">{num(r.piecesCount)}</TableCell>
                          <TableCell align="right">{num(r.totalWeightKg)}</TableCell>
                          <TableCell align="right">{num(r.revenuePaid)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title={cheap ? "Ниже медианы по ₸/кг" : ""} arrow>
                              <span style={{ color: cheap ? "#d98a00" : "inherit", fontWeight: cheap ? 700 : 400 }}>
                                {num(r.revenuePerKg)}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{pct(r.unpaidShare)}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: problem ? "#eb445a" : "inherit", fontWeight: problem ? 700 : 400 }}>
                              {pct(r.problemRate)}
                            </span>
                          </TableCell>
                          <TableCell align="right">{pct(r.deliveredRate)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          {/* Баланс направлений */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Баланс направлений (перекос потоков = риск порожнего пробега)
              </Typography>
              {balance.map((p) => {
                const total = p.abWeightKg + p.baWeightKg || 1;
                const abPct = (p.abWeightKg / total) * 100;
                return (
                  <div key={`${p.aID}|${p.bID}`} style={{ padding: "6px 0" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 4,
                      }}
                    >
                      <span>
                        {p.aName} ⇄ {p.bName}
                      </span>
                      <span>
                        {p.oneDirectional ? (
                          <Chip size="small" color="error" label="одностороннее" />
                        ) : (
                          <Chip
                            size="small"
                            color={p.ratio >= 3 ? "warning" : "default"}
                            label={`×${num(p.ratio)}`}
                          />
                        )}
                      </span>
                    </div>
                    <div style={{ display: "flex", height: 14, borderRadius: 4, overflow: "hidden" }}>
                      <div
                        style={{ width: `${abPct}%`, background: "#3880ff" }}
                        title={`${p.aName}→${p.bName}: ${num(p.abWeightKg)} кг (${p.abTrips})`}
                      />
                      <div
                        style={{ width: `${100 - abPct}%`, background: "#0cd1e8" }}
                        title={`${p.bName}→${p.aName}: ${num(p.baWeightKg)} кг (${p.baTrips})`}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: "#92949c",
                        marginTop: 2,
                      }}
                    >
                      <span>
                        →{p.bName}: {num(p.abWeightKg)} кг
                      </span>
                      <span>
                        →{p.aName}: {num(p.baWeightKg)} кг
                      </span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}

      <RouteDetailDrawer
        selected={selected}
        from={from}
        to={to}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

/** Боковая панель с детализацией одного маршрута. */
const RouteDetailDrawer: React.FC<{
  selected: { originID: string; destID: string; title: string } | null;
  from: string;
  to: string;
  onClose: () => void;
}> = ({ selected, from, to, onClose }) => {
  const { data, isLoading } = useQuery({
    enabled: !!selected,
    queryKey: ["baggage-route-detail", { ...selected, from, to }],
    queryFn: async () =>
      (
        await baggageStatsService.getRouteDetail(selected!.originID, selected!.destID, { from, to })
      ).data,
  });

  const currency = data?.currency || "KZT";
  const num = (n: number) => Number(n || 0).toLocaleString("ru-RU");
  const route = data?.route;

  const dynamicsData = useMemo(() => {
    const d = data?.dynamics || [];
    return {
      labels: d.map((x: any) => x.date),
      datasets: [
        {
          type: "bar" as const,
          label: "Отправки",
          data: d.map((x: any) => x.trips),
          backgroundColor: "rgba(56,128,255,0.55)",
          yAxisID: "y",
        },
        {
          type: "line" as const,
          label: `Выручка, ${currency}`,
          data: d.map((x: any) => x.revenue),
          borderColor: "#2dd36f",
          backgroundColor: "#2dd36f",
          tension: 0.3,
          yAxisID: "y1",
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const dynamicsOptions = useMemo(
    () => ({
      interaction: { mode: "index" as const, intersect: false },
      scales: {
        y: { position: "left" as const, beginAtZero: true },
        y1: { position: "right" as const, beginAtZero: true, grid: { drawOnChartArea: false } },
      },
    }),
    [],
  );

  return (
    <Drawer anchor="right" open={!!selected} onClose={onClose}>
      <Box sx={{ width: { xs: 340, sm: 520 }, p: 2 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {selected?.title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <MdClose />
          </IconButton>
        </div>

        {isLoading || !route ? (
          <div className="text-center m-t-32">
            <CircularProgress />
          </div>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1.5,
                my: 2,
              }}
            >
              {[
                { label: "Отправок", value: num(route.tripsCount) },
                { label: "Мест", value: num(route.piecesCount) },
                { label: "Вес, кг", value: num(route.totalWeightKg) },
                { label: "Выручка", value: `${num(route.revenuePaid)} ${currency}` },
                { label: "₸/кг", value: num(route.revenuePerKg) },
                { label: "Средний чек", value: `${num(route.avgCheck)} ${currency}` },
                { label: "Проблемность", value: `${Math.round(route.problemRate * 100)}%` },
                { label: "Выдано", value: `${Math.round(route.deliveredRate * 100)}%` },
              ].map((c) => (
                <Card key={c.label} variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {c.label}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {c.value}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Динамика по дням
            </Typography>
            <ChartCanvas type="bar" data={dynamicsData} options={dynamicsOptions} height={220} />

            {route.byTrain?.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                  По поездам
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Поезд</TableCell>
                      <TableCell align="right">Отпр.</TableCell>
                      <TableCell align="right">Вес, кг</TableCell>
                      <TableCell align="right">₸/кг</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {route.byTrain.map((t: any) => (
                      <TableRow key={t.trainID}>
                        <TableCell>{t.trainNumber || `#${t.trainID}`}</TableCell>
                        <TableCell align="right">{num(t.tripsCount)}</TableCell>
                        <TableCell align="right">{num(t.totalWeightKg)}</TableCell>
                        <TableCell align="right">{num(t.revenuePerKg)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default RouteAnalytics;
